<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute, RouterLink } from 'vue-router'
import { ExternalLink, FolderDot } from 'lucide-vue-next'

import ReportDataList from '@/pages/Feature/ReportDataList.vue'
import Weather from '@/pages/Feature/Weather.vue'
import Elevation from '@/pages/Feature/Elevation.vue'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Point, LineString, Polygon, MultiPolygon } from '@/components/icons'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { VerifiedMarker } from '@/components/ui/verified-marker'
import { MediaList } from '@/components/media'

import { area } from '@turf/turf'
import { formatTime } from '@/utils/datetime'

const props = defineProps({
  feature: {
    type: Object,
    required: true
  },
  showOpenInProject: {
    type: Boolean,
    default: false
  }
})

const route = useRoute()
const store = useStore()

const settings = computed(() => store.getters['user/getUserSettings'])
const selectedOrg = computed(() => store.getters['org/getSelectedOrg'])

const timeFormat = computed(() => {
  return `MMMM D YYYY, ${settings.value.timeFormat === '12' ? 'h:mm a' : 'H:mm'}`
})

const areaSize = computed(() => {
  if (!props.feature) return
  if (props.feature.geometry.type !== 'Polygon' && props.feature.geometry.type !== 'MultiPolygon') return

  const areaInSm = area(props.feature)
  const areaInHa = areaInSm / 10000
  const areaInSkm = areaInSm / 1000000
  return {
    sm: (areaInSm).toFixed(4),
    ha: (areaInHa).toFixed(4),
    skm: (areaInSkm).toFixed(4)
  }
})

const iconName = computed(() => {
  return props.feature.geometry.type.toLowerCase()
})

const createdAt = computed(() => {
  let created_origin = props.feature.properties.created_origin
  let created_at = props.feature.properties.created_at
  if (created_origin) {
    if (created_origin.toString().length === 10) {
      created_origin = created_origin * 1000
    }
    return formatTime(created_origin, settings.value.tz, timeFormat.value)
  }
  return formatTime(created_at, settings.value.tz, timeFormat.value)
})

const syncedAt = computed(() => {
  return formatTime(props.feature.properties.created_at, settings.value.tz, timeFormat.value)
})

const updatedAt = computed(() => {
  return formatTime(props.feature.properties.updated_at, settings.value.tz, timeFormat.value)
})

const projectName = computed(() => {
  if (props.feature.properties.project_names?.length > 0) {
    return props.feature.properties.project_names[0]
  }
  return props.feature.properties.project_id || null
})

const exportGeojson = () => {
  const geoJSON = {
    type: 'FeatureCollection',
    features: [props.feature]
  }
  const fileName = props.feature.properties.name
  const geoJSONString = JSON.stringify(geoJSON, null, 2)
  const blob = new Blob([geoJSONString], { type: "application/json" })
  const link = document.createElement('a')

  const url = URL.createObjectURL(blob)
  link.href = url
  link.download = fileName + '.geojson'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const featureProjectId = computed(() => {
  return props.feature.properties.project_id ||
    (props.feature.properties.project_ids?.length > 0 ? props.feature.properties.project_ids[0] : null)
})

const featureRoute = computed(() => {
  const orgId = selectedOrg.value?.organization_id || route.params?.org_id
  if (!orgId || !featureProjectId.value) return null

  return {
    name: 'Feature',
    params: {
      org_id: orgId,
      featureCode: props.feature.properties.code,
      project_id: featureProjectId.value
    }
  }
})
</script>

<template>
  <div v-if="feature && feature.properties">
    <div v-if="showOpenInProject && featureRoute" class="mb-3">
      <RouterLink :to="featureRoute" class="inline-flex items-center justify-center w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground">
        <ExternalLink class="w-4 h-4 mr-2" />
        Open in Project
      </RouterLink>
    </div>

    <div v-if="projectName" class="flex items-center mb-2">
      <FolderDot class="mr-0.5 w-4 h-4 text-muted-foreground" />
      <span class="text-muted-foreground">{{ projectName }}</span>
    </div>

    <div class="mb-3">
      <div class="flex items-center">
        <div class="mr-2 w-8 h-8 rounded-full shadow-sm flex items-center justify-center" :style="{'background-color': feature.properties.color}">
          <div v-if="feature.properties?.s3_icon">
            <img class="object-contain w-5 h-5" :src="feature.properties?.s3_icon" alt="">
          </div>
          <div v-else>
            <Point v-if="iconName === 'point'" class="fill-white" />
            <LineString v-if="iconName === 'linestring'" class="fill-white" />
            <Polygon v-if="iconName === 'polygon'" class="fill-white" />
            <Polygon v-if="iconName === 'geometrycollection'" class="fill-white" />
            <MultiPolygon v-if="iconName === 'multipolygon'" class="fill-white" />
          </div>
        </div>
        <h3 class="font-bold text-lg">{{ feature.properties.name }}</h3>
      </div>
    </div>

    <p v-if="feature.properties.description" class="mb-3">
      {{ feature.properties.description }}
    </p>

    <div v-if="feature.properties.tags && feature.properties.tags.length > 0" class="mb-3">
      <h3 class="text-sm font-semibold mb-1">Tags</h3>
      <div class="flex flex-wrap gap-1">
        <Badge v-for="(tag, index) in feature.properties.tags" :key="index" variant="outline" class="text-xs">
          {{ tag.k }}: {{ tag.v }}
        </Badge>
      </div>
    </div>

    <div v-if="feature.properties.shared" class="mb-3">
      <div class="flex items-center">
        <Badge variant="secondary" class="text-xs">Public</Badge>
      </div>
    </div>

    <ul class="text-muted-foreground mb-4">
      <li>Created: {{ createdAt }}</li>
      <li>Synced: {{ syncedAt }}</li>
      <li class="mb-1" v-if="feature.properties.updated_at">Updated: {{ updatedAt }}</li>
      <li>Geometry: {{ feature.geometry.type }}</li>
      <li v-if="areaSize">Size in km²: {{ areaSize.skm }} km²</li>
      <li v-if="areaSize">Size in ha: {{ areaSize.ha }} ha</li>

    </ul>

    <div v-if="feature.properties.member_display_name" class="flex justify-between items-start mt-2 mb-4">
      <div class="flex justify-start items-center">
        <Avatar class="w-6 h-6">
          <AvatarImage
            v-if="feature.properties.member_avatar_url"
            :src="feature.properties.member_avatar_url"
            :alt="feature.properties.member_display_name"
          />
          <AvatarFallback></AvatarFallback>
        </Avatar>
        <span class="ml-2">{{ feature.properties.member_display_name }}</span>
        <VerifiedMarker v-if="feature.properties.member_verified" class="ml-1 flex-shrink-0" />
      </div>
    </div>

    <div class="mb-4" v-if="feature.properties.data?.length > 0">
      <h3 class="mb-1 text-base font-bold">Collected data</h3>
      <ReportDataList :data="feature.properties.data" />
    </div>

    <div v-if="feature.properties?.resources && feature.properties.resources.length > 0" class="mb-4">
      <h3 class="text-base font-bold mb-2">Media</h3>
      <MediaList
        :resources="feature.properties.resources"
        :show-delete="false"
      />
    </div>

    <div class="mb-4" v-if="feature.geometry.type === 'Point' || (feature.geometry.type === 'LineString' && feature.geometry.coordinates.length <= 10)">
      <div class="flex items-center justify-start">
        <h3 class="mb-1 text-base font-bold">Elevation</h3>
      </div>
      <Elevation :geometry="feature.geometry" :show-chart="false" :key="feature.properties.code" />
    </div>

    <div class="mb-4">
      <div class="flex items-center justify-start">
        <h3 class="mb-1 text-base font-bold">Weather</h3>
      </div>
      <Weather :code="feature.properties.code" :key="feature.properties.code" />
    </div>

    <div class="mb-4 text-muted-foreground">
      <h3 class="mb-0 font-bold text-base">Other</h3>
      <div class="mb-2">ID: {{ feature.properties.code }}</div>
      <Button size="sm" variant="secondary" @click="exportGeojson">Export Geojson</Button>
    </div>
  </div>
</template>
